import { Spinner } from "@/components/ui/v2/spinner";
import {
  MsApplyOvertimePage,
  MsAttendanceExceptionsList,
  MsAttendanceExceptionView,
  MsAttendanceSummaryPage,
  MsOvertimeDetailPage,
  MsOvertimePage,
  MsRegularizationList,
  MsTimeAttendanceLayout,
  UpsertRegularization,
  ViewRegularization,
} from "@/utils/lazyLoadComponents";
import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { RequireTimeAttendanceAccess } from "@/middleware";
import { addSlashes } from "../route.utils";

export const MS_TIME_ATTENDANCE_DYNAMIC_ID_KEY = "id";

export enum MS_TIME_ATTENDANCE_PATH_SEGMENTS {
  ROOT = "time-attendance",
  ATTENDANCE_SUMMARY = "attendance-summary",
  REGULARIZATION = "regularization",
  ADD = "add",
  EDIT = "edit",
  OVERTIME = "overtime",
  ATTENDANCE_EXCEPTIONS = "attendance-exceptions",
  DETAILS = `:${MS_TIME_ATTENDANCE_DYNAMIC_ID_KEY}`,
}

export const myspaceTimeAttendanceRoutes: RouteObject = {
  path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Time & Attendance" },
  HydrateFallback: Spinner,
  // Permission gate for the whole /time-attendance/* subtree (tabs + detail
  // routes). Redirects users without individual Time Attendance "view" to Home.
  // Renders <Outlet/>, so the stats+tabs layout still wraps only the tab pages.
  Component: RequireTimeAttendanceAccess,
  children: [
    // ── Page-level tabs layout: each tab owns its own content ───────────────
    {
      Component: MsTimeAttendanceLayout,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(MS_TIME_ATTENDANCE_PATHS.ATTENDANCE_SUMMARY),
        },
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_SUMMARY,
          handle: { breadcrumb: "Attendance Summary" },
          Component: MsAttendanceSummaryPage,
        },
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
          handle: { breadcrumb: "Regularization" },
          Component: MsRegularizationList,
        },
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
          handle: { breadcrumb: "Overtime" },
          Component: MsOvertimePage,
        },
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
          handle: { breadcrumb: "Attendance Exceptions" },
          Component: MsAttendanceExceptionsList,
        },
      ],
    },

    // ── Regularization detail/form routes (no stats, no tabs) ──────────────
    {
      path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
      handle: { breadcrumb: "Regularization" },
      children: [
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
          handle: { breadcrumb: "Add" },
          Component: UpsertRegularization,
        },
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS,
          loader: ({ params }) => ({
            breadcrumbLabel: `Request #${params[MS_TIME_ATTENDANCE_DYNAMIC_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel ?? "Request",
          },
          Component: ViewRegularization,
        },
        {
          path: `${MS_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS}/${MS_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: UpsertRegularization,
        },
      ],
    },

    // ── Overtime detail/form routes (no stats, no tabs) ────────────────────
    {
      path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
      handle: { breadcrumb: "Overtime" },
      children: [
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
          handle: { breadcrumb: "Request Overtime" },
          Component: MsApplyOvertimePage,
        },
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS,
          loader: ({ params }) => ({
            breadcrumbLabel: `Request #${params[MS_TIME_ATTENDANCE_DYNAMIC_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel ?? "Request",
          },
          Component: MsOvertimeDetailPage,
        },
        {
          path: `${MS_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS}/${MS_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: MsOvertimeDetailPage,
        },
      ],
    },

    // ── Attendance exceptions detail routes (no stats, no tabs) ─────────────
    {
      path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
      handle: { breadcrumb: "Attendance Exceptions" },
      children: [
        {
          path: MS_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Exception Details" },
          Component: MsAttendanceExceptionView,
        },
      ],
    },
  ],
};

export const MS_TIME_ATTENDANCE_PATHS = Object.freeze({
  ROOT: addSlashes(MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT),
  ATTENDANCE_SUMMARY: addSlashes(
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_SUMMARY,
  ),
  REGULARIZATION: addSlashes(
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
  ),
  ADD_REGULARIZATION: addSlashes(
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
  ),
  VIEW_REGULARIZATION: (id: string | number) =>
    addSlashes(
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
      String(id),
    ),
  EDIT_REGULARIZATION: (id: string | number) =>
    addSlashes(
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
      String(id),
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT,
    ),
  OVERTIME: addSlashes(
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
  ),
  ADD_OVERTIME: addSlashes(
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
  ),
  VIEW_OVERTIME: (id: string | number) =>
    addSlashes(
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
      String(id),
    ),
  EDIT_OVERTIME: (id: string | number) =>
    addSlashes(
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
      String(id),
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT,
    ),
  ATTENDANCE_EXCEPTIONS: addSlashes(
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
    MS_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
  ),
  VIEW_EXCEPTION: (id: string | number) =>
    addSlashes(
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      MS_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
      String(id),
    ),
});
